export const edgeInfrastructureSolutions = {
  'ai-infrastructure': {
    title: 'AI基础设施解决方案',
    background:
      '人工智能技术飞速发展与产业数字化深化，各类 AI 应用广泛落地，行业算力需求大幅提升。传统 IT 架构存在算力分散、扩展性弱等短板，难以匹配 AI 高算力、低时延的运行要求。因此，建设集约高效、弹性可靠的 AI 基础设施，是企业智能化转型、支撑 AI 业务规模化发展的必要基础。',
    challenges: [
      { title: '1. 平台架构融合不足', desc: '现有 AI 业务平台与企业传统基础设施相互独立、架构割裂，软硬件资源缺乏有效打通与深度融合，各系统烟囱化问题突出，缺少一体化联动协同机制，无法形成统一的底层承载体系，制约整体智能化业务协同运转。' },
      { title: '2. 模型管理体系零散', desc: '随着 AI 应用不断落地，各类算法模型、大模型数量持续增加，企业缺乏集中化、标准化的模型管控平台。模型从研发、上线、迭代到退役全流程缺少规范管理，版本混乱、权限分散，难以实现统一管控与安全合规使用。' },
      { title: '3. 推理服务承载薄弱', desc: '各业务线 AI 推理需求快速增长，推理服务多为分散部署、独立运行，缺乏统一的发布、运维与负载管理体系。面对高并发、多场景业务访问，服务扩容能力不足，易出现运行不稳定、响应延迟等问题，业务承载能力受限。' },
      { title: '4. 算力资源调度低效', desc: '企业算力资源分散部署，资源划分固化、共享程度低，存在资源闲置与算力短缺并存的问题。缺少智能化动态调度能力，无法根据 AI 训练、推理等不同业务负载按需分配资源，算力利用率低，难以适配 AI 业务弹性波动的运行需求。' },
    ],
    scenarios: [
      { title: '一、大模型训练与迭代场景', desc: '适用于科技企业、科研机构的大模型研发，AI基础设施提供高性能算力、分布式存储及高速网络，支持算力弹性扩容，适配模型预训练、微调至迭代全流程，缩短研发周期、降低技术门槛与成本。' },
      { title: '二、AI推理服务规模化部署场景', desc: '面向需规模化落地AI应用的行业，AI基础设施搭建统一推理服务平台，实现模型集中管理、快速部署与负载均衡，适配高并发、低时延需求，保障推理服务稳定高效，支撑AI应用规模化落地。' },
      { title: '三、企业级AI一体化运维场景', desc: '针对大型企业多业务线AI并行场景，AI基础设施整合模型管理、算力调度、运维监控能力，实现AI业务全生命周期一体化运维，简化流程、降低运维成本，保障AI业务稳定运行。' },
      { title: '四、行业智能场景集约化承载', desc: '适配各行业智能化转型，AI基础设施提供集约化底层承载，整合行业数据、算法与算力资源，支撑各类行业智能应用快速落地，打破资源壁垒，实现AI应用规模化、标准化部署。' },
    ],
    architectureAsset: 'ai-infra-architecture.png',
    advantages: [
      { title: '1. 多场景灵活部署', desc: '支持灵活部署，兼容主流操作系统和 Kubernetes 集群，适配多种基础设施环境，覆盖从开发测试、轻量级边缘部署（适合中小尺寸模型）到数据中心（适合大尺寸模型）部署等多样化场景需求。' },
      { title: '2. 完整生产级基础设施', desc: 'AI 平台与云平台深度融合，统一构建异构基础架构，灵活承载混合 AI 负载；配套高性能存储覆盖 AI 全生命周期数据读写，结合微隔离防护与统一可观测体系，打造高可用、智能化的 AI 生产环境。' },
      { title: '3. 覆盖多样 AI 业务需求', desc: '为 AI 应用提供完整的基础设施解决方案，支撑企业从内部提效（如知识库、智能助手）到业务赋能（如医疗导诊、智能投研）等各类应用场景。' },
      { title: '4. 弹性算力调度与成本优化', desc: '内置智能算力调度引擎，可动态调度异构算力、平衡负载，破解资源错配痛点、提升算力利用率；支持按需弹性扩缩容与按量计费，降低一次性投入成本，灵活适配业务增长节奏。' },
    ],
    caseBackground:
      '某知名电子商务服务与解决方案提供商，为海量电商商户提供全链路数字化服务，业务覆盖智能商品推荐、智能客服、营销流量预测、交易风控等多个 AI 应用场景。随着企业 AI 能力规模化落地，原有分散化的 AI 部署架构逐渐暴露短板：训练、微调、推理任务独立建设，资源无法互通调度；同时电商业务天然存在极强的流量波动特性，大促高峰期推理服务并发量可暴涨 10 倍以上，闲时则需开展大模型迭代训练，两类业务资源需求冲突严重。此外，电商用户交易、行为数据敏感度高，企业明确要求所有 AI 能力必须在私有化环境中部署，严禁数据流出内网。为破解上述痛点，企业启动 AI 基础设施升级，构建统一的企业级 AI 支撑能力。',
    caseDifficulties: [
      '峰谷差异下的多任务调度难',
      '电商业务流量峰谷差距极大，大促高峰期推理服务算力需求暴增，日常闲时则需占用资源开展模型训练、微调，原有架构无法统一调度三类工作负载，导致资源忙闲不均，大促易出现推理延迟、闲时算力大量闲置。',
      '混合异构架构的兼容适配难',
      '企业存量 IT 架构既有承载成熟业务的传统虚拟机集群，也有新建的 Kubernetes 容器集群，两类架构资源孤立无法共享，且存量业务不能中断，改造过程中需兼顾架构兼容与业务连续性，实现难度较高。',
      'AI 全流程能力割裂效率低',
      '原有 AI 能力烟囱式建设，训练、微调、推理各环节独立部署，流程割裂，模型从训练完成到上线部署需大量人工适配，迭代周期长达数周，无法快速响应电商业务的敏捷 AI 需求。',
      '私有化场景下的性能安全平衡难',
      '受数据合规要求限制，所有 AI 能力必须私有化部署，不能依赖公有云弹性资源，如何在私有化封闭环境中，既保障数据安全合规，又兼顾 AI 训练、推理的高性能运行，成为核心挑战。',
    ],
    caseSolutions: [
      '统一算力调度，适配弹性业务波动',
      '部署轻量化 AI 基础设施的统一算力调度引擎，将训练、微调、推理任务纳入统一调度体系，基于电商业务流量的峰谷规律实现动态资源分配：大促高峰期自动将闲置算力向推理服务倾斜，保障高并发下的毫秒级响应；日常闲时则调度空闲资源支撑模型训练、微调任务，算力利用率提升 40% 以上，同时支持推理服务秒级弹性扩缩容，应对突发流量冲击。',
      '混合负载纳管，兼容存量异构架构',
      '方案原生支持虚拟机与 Kubernetes 集群的统一纳管，无需改造企业原有存量 IT 架构，即可将两类架构的算力、存储资源池化，形成统一的共享资源池，AI 任务可根据自身需求灵活选择运行环境，实现跨架构的资源共享与任务调度，既保护了企业原有 IT 投资，又实现了混合工作负载的统一承载。',
      '端到端流程贯通，覆盖 AI 全生命周期',
      '方案搭建了覆盖模型训练、微调、推理的端到端一体化工作流，打通各环节的数据与能力壁垒，支持模型从训练产出、版本管理到一键部署为推理服务的全流程自动化，将模型上线周期从数周缩短至小时级，同时可统一管理各类 AI 服务，高效支撑企业多样化的 AI 业务需求。',
      '私有化原生部署，兼顾安全与性能',
      '方案采用全私有化部署模式，所有 AI 能力、业务数据均留存于企业内网，内置数据加密、细粒度 RBAC 权限管控、全链路操作审计等能力，满足电商数据安全与合规要求；同时配套高性能分布式存储与网络优化能力，保障私有化环境下模型训练、推理的高性能运行，实现了安全合规与业务性能的双向兼顾。',
    ],
  },
  'server-virtualization': {
    title: '服务器虚拟化解决方案',
    background:
      '随着企业数字化转型的深入推进，业务系统数量持续增长，传统物理服务器单机部署模式的短板逐渐凸显：资源利用率低下、业务交付周期长、运维成本居高不下，同时企业 IT 架构逐步向异构化演进，既有传统 x86 服务器，也有国产化替代新增的飞腾、海光等架构服务器，不同架构的资源孤立割裂，无法统一调度。基于瑞和云架构的服务器虚拟化方案，依托瑞和云 “云翼” 云计算管理平台，通过虚拟化技术将底层物理硬件资源抽象为弹性可调度的虚拟资源，实现资源池化、管理统一化，帮助企业盘活存量 IT 资产，降本增效，支撑业务高效敏捷运行。',
    challenges: [
      { title: '资源利用率低下、投入浪费严重', desc: '传统单机部署模式下，业务独占物理服务器，资源忙闲不均，多数服务器 CPU 利用率不足 20%，大量算力、存储资源长期闲置，硬件采购、机房能耗、运维人力等成本投入浪费严重。' },
      { title: '业务交付效率低、响应能力不足', desc: '新业务上线需经历硬件采购、上架、系统配置等全流程，周期长达 2-3 周，无法快速响应业务部门的迭代需求，严重制约了业务的敏捷性与市场响应速度。' },
      { title: '异构资源管理割裂、运维复杂度高', desc: '企业 IT 架构逐步异构化，存量环境同时存在 x86、国产化两类硬件架构，以及 ESXi、KVM 等多种虚拟化技术，不同体系的资源独立运维，管理工具割裂，运维人员需掌握多套运维体系，工作压力大、效率低。' },
      { title: '业务可靠性保障弱、故障恢复慢', desc: '单机部署模式下，服务器硬件故障会直接导致业务中断，缺乏有效的高可用保障机制，故障排查与恢复时间长达数小时，核心业务中断风险高。' },
    ],
    scenarios: [
      { title: '1、传统业务虚拟化迁移场景', desc: '支撑企业将 ERP、OA、CRM 等传统业务系统，从物理服务器平滑迁移至虚拟化平台，无需改造业务系统，即可实现资源整合与效率提升。' },
      { title: '2、多租户资源承载场景', desc: '为运营商客户提供多租户隔离的虚拟资源服务，支撑运营商为政企客户提供弹性云主机服务，满足租户的快速部署与安全合规需求。' },
      { title: '3、异构资源统一纳管场景', desc: '整合企业存量的 x86、国产化服务器，以及不同虚拟化技术的资源，实现统一管理，盘活存量 IT 资产，降低运维复杂度。' },
      { title: '4、开发测试环境快速交付场景', desc: '为研发团队提供快速创建、销毁虚拟机的能力，支撑开发测试环境的快速搭建与迭代，提升研发效率，缩短业务上线周期。' },
    ],
    architectureAsset: 'server-virtualization-architecture.png',
    advantages: [
      { title: '异构资源全面兼容，盘活存量资产', desc: '原生支持 x86 与飞腾、海光等国产化架构服务器，兼容 ESXi、KVM、PowerVM 等多种虚拟化技术，实现异构资源统一纳管，无需替换存量设备，充分保护企业原有 IT 投资。' },
      { title: '资源利用率大幅提升，降低投入成本', desc: '通过资源池化与智能动态调度，将物理服务器平均利用率从不足 20% 提升至 60% 以上，大幅减少资源闲置，降低硬件采购、机房能耗等成本，整体 TCO 降低 40% 以上。' },
      { title: '业务交付效率显著提升，支撑敏捷迭代', desc: '支持虚拟机模板化快速部署，新业务上线周期从 2-3 周缩短至分钟级，同时支持弹性扩缩容，快速响应业务波动需求，大幅提升业务敏捷性。' },
      { title: '高可靠保障，业务连续运行', desc: '提供虚拟机热迁移、HA 高可用、快照备份、灾备等能力，物理服务器故障时可自动迁移虚拟机，业务无感知中断，RTO 缩短至分钟级，有效保障核心业务连续运行，系统可用性可达 99.95%。' },
    ],
    caseBackground:
      '某省级电信运营商，随着政企云业务的快速发展，需要为旗下政企客户提供安全、弹性的云主机服务，同时整合自身内部分散的 IT 资源。原有架构下，运营商的 IT 资源存在严重的割裂问题：既有大量存量 x86 服务器，也有国产化替代新增的飞腾服务器，同时存量环境同时存在 VMware、KVM 两种虚拟化平台，不同资源无法统一调度；此外，传统虚拟化方案缺乏多租户安全隔离与合规审计能力，无法满足政企客户的安全与合规要求。为破解上述痛点，该运营商基于瑞和云架构部署了服务器虚拟化方案，构建统一的政企云主机服务平台。',
    caseDifficulties: [
      '异构资源无法统一管理调度：存量资源涵盖 x86、飞腾两类硬件架构，以及 VMware、KVM 两种虚拟化技术，不同资源独立管理，无法统一调度，出现部分资源闲置、部分业务资源不足的矛盾。',
      '多租户安全与合规要求严苛：面向政企客户提供云服务，需要保障不同租户间的资源、数据完全隔离，同时满足运营商行业的等保合规与审计要求，原有方案缺乏对应的安全管控能力。',
      '业务交付效率低，客户体验差：原有模式下，为政企客户交付云主机需要人工协调不同资源池的资源，完成配置与网络打通，交付周期长，无法满足客户快速上线的需求。',
      '业务可靠性不足，中断风险高：原有架构缺乏高可用保障机制，服务器硬件故障会直接导致租户业务中断，故障恢复时间长，无法满足政企客户的业务连续运行要求。',
    ],
    caseSolutions: [
      '异构资源统一纳管，构建共享资源池',
      '基于瑞和云 “云翼” 平台，将运营商的 x86、飞腾服务器，以及 VMware、KVM 虚拟化资源统一接入，实现全量资源的池化，资源可跨架构、跨平台统一调度，利用率提升至 62%。',
      '内置安全能力，满足隔离与合规要求',
      '依托瑞和云内置的安全管控能力，实现多租户间的网络、资源、数据的逻辑隔离，保障租户业务完全独立安全，同时满足合规要求。',
      '模板化快速交付，提升业务响应速度',
      '基于瑞和云平台的虚拟机模板能力，实现云主机的一键创建、网络与配置自动完成，将政企客户的云主机交付周期从原来的 3 天缩短至 15 分钟，业务响应效率提升 90% 以上，大幅提升了客户满意度。',
      '高可用能力，保障业务连续运行',
      '为所有租户虚拟机提供热迁移、HA 高可用能力，当物理服务器出现故障时，可在 3 分钟内自动将虚拟机迁移至其他正常节点，可用性提升至 99.95%，有效降低了业务中断风险。',
    ],
  },
  'edge-computing': {
    title: '边缘计算解决方案',
    background:
      '随着5G、物联网等技术融合，各行业边缘场景产生海量数据，对数据处理的实时性、低时延、安全性需求提升，传统集中式架构存在时延高、安全风险大等问题。基于瑞和云架构的边缘计算方案，依托“云翼”管理平台构建“云-边-端”一体化架构，将核心能力下沉至边缘，联动云端实现全局管控，适配多行业边缘智能化落地需求。',
    challenges: [
      { title: '低时延与高可靠难以兼顾', desc: '边缘场景（如工业设备控制、电力故障预警、车联网）对数据处理时延要求达到毫秒级，同时边缘节点多处于复杂环境，易出现网络中断、设备故障等问题，传统架构难以在保障低时延的同时，实现业务连续运行与数据不丢失。' },
      { title: '边缘资源管控难度大', desc: '边缘节点分布分散、数量众多，且多采用异构硬件（x86、鲲鹏、海光、飞腾等），资源配置参差不齐，缺乏统一的管控平台，导致边缘资源利用率低、运维成本高，无法实现资源的动态调度与全局协同。' },
      { title: '云边协同能力不足', desc: '边缘节点与瑞和云中心平台数据同步不及时，模型下发、策略更新滞后，边缘侧产生的海量数据无法高效回传至中心进行深度分析，同时中心平台无法实时监控边缘节点运行状态，难以实现“云控边、边联端”的协同闭环。' },
      { title: '部署与运维成本高', desc: '边缘节点部署环境复杂，部分节点无专业运维人员，传统边缘方案部署流程繁琐、兼容性差，且故障排查难度大，需投入大量人力物力，导致企业边缘智能化转型成本居高不下。' },
    ],
    scenarios: [
      { title: '电力配网智能运维场景', desc: '适配电力行业配电网柱上变压器等设备的运维需求，在边缘节点部署瑞和云边缘一体机，结合双光谱成像监控技术，实现设备温度实时采集、故障智能预警、数据就地分析，同时联动瑞和云中心平台，完成运维数据汇总、设备健康状态评估，支撑电力运维“早发现、早预警、早处置”，保障供电安全可靠。' },
      { title: '工业智能制造场景', desc: '部署在工业车间边缘，对接生产设备、传感器等终端，实时采集生产数据、设备运行参数，通过边缘节点就地完成数据清洗、分析与异常检测，实现生产过程实时监控、设备故障预判，同时将核心数据回传至瑞和云中心，支撑生产全局优化与智能决策，适配工业机器视觉、AGV调度等细分场景。' },
      { title: '运营商政企边缘服务场景', desc: '为运营商政企客户提供边缘云主机、容器编排等服务，依托瑞和云边缘节点，实现政企业务的低时延部署，支持5G、固网、工业网关等多接入方式，满足政企客户对业务本地部署、数据安全隔离的需求，同时通过瑞和云中心平台实现多租户统一管控与合规审计。' },
      { title: '智慧政务/园区场景', desc: '在政务大厅、产业园区部署边缘节点，对接监控设备、IoT终端，实现人员流动监测、环境参数采集、安全隐患排查等功能，数据就地处理降低时延，同时联动瑞和云中心完成数据汇总与统一管理，支撑智慧园区、智慧安防等场景的智能化升级。' },
      { title: '智慧水务/环保场景', desc: '在污水处理厂、环保监测点部署边缘终端，实时采集水质、污染物等监测数据，通过边缘节点就地分析数据异常，生成预警信息，同时将监测数据同步至瑞和云中心，实现环保监测全流程可视化、可追溯，助力环保治理提质增效。' },
    ],
    architectureAsset: 'edge-computing-architecture.png',
    advantages: [
      { title: '云边协同高效，时延大幅降低', desc: '依托瑞和云“云-边-端”一体化架构，实现边缘数据就地处理，核心业务时延降低至毫秒级，大幅减少数据回传带宽成本，同时边缘节点与中心平台实时协同，模型下发、策略更新响应迅速，兼顾低时延与全局管控需求，适配高实时性业务场景。' },
      { title: '异构资源兼容，部署灵活便捷', desc: '原生支持x86、鲲鹏、海光、飞腾等多种异构硬件架构，兼容各类终端设备与协议，可无缝对接企业现有IT基础设施，无需替换存量设备；支持轻量化部署，边缘节点可根据场景需求灵活选择单机部署、集群部署，适配从工业现场、偏远区域到城市园区的各类部署环境，最小可支持单节点交付。' },
      { title: '统一管控运维，成本显著优化', desc: '通过瑞和云“云翼”管理平台，实现所有边缘节点、资源、业务的集中管控与可视化监控，支持故障自动告警、远程排查、批量运维，无需专业人员现场值守，大幅降低运维成本；同时通过资源池化与动态调度，提升边缘资源利用率，降低硬件采购与能耗成本，整体TCO降低35%以上。' },
      { title: '场景适配性强，可扩展性突出', desc: '方案可灵活适配电力、工业、运营商、政务等多行业边缘场景，支持AI推理、数据采集、实时监控等多样化业务需求；基于瑞和云模块化架构，可根据业务增长灵活扩展边缘节点数量与功能，支持边缘计算能力与AI、数字孪生等技术深度融合，支撑业务长期演进。' },
    ],
    caseBackground:
      '某省级电力公司，负责辖区内配电网的建设、运维与管理，辖区内配电网柱上变压器数量众多，分布广泛，部分设备位于偏远区域，传统运维模式存在明显短板：设备运行状态监测依赖人工巡检，效率低下、成本高，且无法实时发现设备故障；设备运行数据需回传至中心平台处理，时延较高，导致故障处置不及时，影响供电可靠性；同时设备数据涉及电力安全敏感信息，需满足数据本地化处理与合规审计要求。为破解上述痛点，该电力公司基于瑞和云架构部署边缘计算方案，构建配电网智能运维体系，实现柱上变压器运维的数字化、智能化升级。',
    caseDifficulties: [
      '时延与可靠性矛盾突出：柱上变压器故障预警、温度监测等业务需毫秒级数据处理响应，传统集中式架构数据回传时延过高，且偏远区域网络不稳定，易导致数据丢失，影响故障处置效率。',
      '边缘节点管控难度大：辖区内边缘节点分布分散、数量多，且部分节点部署在无专业运维人员的偏远区域，设备类型包括多种异构硬件，缺乏统一的管控手段，运维成本高、效率低。',
      '安全合规要求严苛：电力设备运行数据属于敏感信息，需实现数据本地化存储与处理，同时需满足电力行业等保合规要求，边缘节点暴露在开放环境中，安全防护压力大。',
      '场景适配性不足：配电网运维需结合双光谱成像、温度监测等专属需求，传统边缘方案无法灵活适配电力行业专属场景，且难以与现有电力运维平台无缝对接。',
    ],
    caseSolutions: [
      '构建云边协同架构，降低时延保障可靠：基于瑞和云架构，在辖区内各配电网区域部署瑞和云边缘一体机，搭载双光谱成像分析组件，实现柱上变压器温度数据、运行状态的就地采集与实时分析，故障预警时延降低至50毫秒以内；边缘节点支持离线运行，配备不间断电源，可实现无网无电情况下6小时持续监控，网络恢复后自动同步数据至瑞和云中心，保障业务连续性。',
      '统一管控平台，优化运维效率：通过瑞和云“云翼”管理平台，将所有边缘节点统一接入，实现节点运行状态、资源利用率、设备监测数据的可视化监控；支持故障自动告警、远程排查与批量策略下发，无需专业人员现场值守，将边缘节点运维效率提升60%以上，同时实现异构硬件资源的统一调度，资源利用率提升至70%。',
      '强化安全合规，保障数据安全：方案采用全私有化部署模式，所有电力设备数据均在边缘节点就地存储、处理，避免数据外传；边缘节点配备端口自闭锁、数据加密传输等安全能力，结合瑞和云中心的细粒度权限管控与全链路审计功能，满足电力行业等保2.0三级合规要求，有效防范外部恶意攻击与数据泄露。',
      '定制场景适配，实现无缝对接：结合电力配网运维专属需求，定制开发边缘计算分析组件，支持红外光热成像、可见光双光谱监控，可自动分析设备温度变化趋势、识别故障隐患，实现故障提前预警；同时打通与电力公司现有运维平台的接口，实现数据互通与业务协同，无需改造原有系统，快速完成方案落地。',
    ],
  },
  'vm-container-fusion': {
    title: '超融合解决方案',
    background: '数字经济快速发展背景下，云原生、AI技术广泛应用，企业IT架构向敏捷化、一体化转型，需同时承载虚拟机（稳态业务）与容器（敏态创新应用）双轨运行，而传统IT基础设施中各组件分散部署，形成资源孤岛，不仅导致运维复杂、成本偏高，还存在虚拟机与容器网络不通、无法满足AI应用资源交付及数据协同需求，加之安全威胁频发，融合基础设施成为必然趋势，因此VCCI解决方案应运而生，其在单一系统中集成全栈组件，可实现统一管控、网络互通，为AI应用提供支撑，助力企业降本增效、推进数字化转型。',
    challenges: [
      { title: '基础设施管理割裂，运维成本居高不下', desc: '虚拟机与容器环境独立，管理平台与技术栈不同，运维门槛、成本高，故障定位难。' },
      { title: '（二）虚拟机与容器网络隔离，业务交互受阻', desc: '两者网络体系独立，缺乏互联互通机制，业务通信不畅，全网管控和安全防护难度大。' },
      { title: '（三）资源调度低效，无法适配AI应用需求', desc: '资源调度独立、交付繁琐，无法满足AI应用快速部署需求，传统存储难以适配其数据需求。' },
      { title: '（四）混合工作负载适配不足，业务演进受阻', desc: '传统架构无法深度融合两种环境，企业面临容器化难题或需维护两套基础设施，阻碍业务迭代。' },
    ],
    scenarios: [
      { title: '（一）AI应用全生命周期部署场景', desc: '适配各类AI应用，可快速交付资源、调度算力、满足数据存储需求，突破基础设施瓶颈。' },
      { title: '（二）企业混合工作负载承载场景', desc: '支撑 legacy 系统与新兴容器化业务并存，实现业务渐进式现代化，规避技术风险。' },
      { title: '（三）企业级IT基础设施集约化管理场景', desc: '整合多组件实现集约化部署，简化管理、降低运维与建设成本，支持按需扩展。' },
      { title: '（四）分布式办公与多集群管理场景', desc: '支持跨区域多集群统一管理，实现资源协同联动与动态扩缩容，降低管理复杂度。' },
    ],
    architectureAsset: 'vm-container-fusion-architecture.png',
    advantages: [
      { title: '简化管理', desc: '一套架构实现基础设施的统一管理，简单易用，运维门槛低。' },
      { title: '提升效率', desc: '平台上线快，Kubernetes 集群交付速度快。应用快速迭代，交付效率高。' },
      { title: '强大可靠', desc: '平台稳定可靠、性能卓越，提升应用可靠性，保证业务连续性。' },
      { title: '节省成本', desc: '最小三个节点即可构建整个平台，可按需扩展。基础设施统一管理，提升资源利用率。' },
    ],
    caseBackground:
      '某中型制造企业推进数字化转型，同时运行虚拟机部署的ERP、生产管理等legacy系统，及容器化部署的AI质检、数字化营销等新兴业务，原有割裂架构导致运维繁琐、资源浪费，且跨环境业务通信受阻。',
    caseDifficulties: [
      '一、虚拟机与容器网络隔离，AI质检数据无法顺畅同步至虚拟机内的生产管理系统；',
      '二、两套环境独立管理，运维成本偏高，资源调度低效；',
      '三、AI应用需快速扩容，传统存储无法适配其海量数据存储需求。',
    ],
    caseSolutions: [
      '部署虚拟化容器融合基础设施方案整合多组件，采用扁平网络打通虚拟机与容器互联互通壁垒，实现数据顺畅流转；',
      '通过统一管理平台简化运维，实现资源统一调度；',
      '利用分布式存储满足AI应用数据需求，支持快速扩容，同时降低整体IT成本。',
    ],
  },
  'database-cloud': {
    title: 'Paas服务解决方案',
    background:
      '数据库作为企业 IT 应用的核心，在企业数据中心架构的云化转型中至关重要。当前，用户在数据中心部署数据库系统，主要采用传统集成架构、虚拟化部署模式和数据库一体机等方式，无法兼顾客户在高性能、稳定性、易管理性、敏捷性等方面的需求。',
    challengesDescription:
      '目前随着企业数字化转型进程的加快，企业对数据库需求不断增加，类型不断增多，敏捷性要求更高，同时要求更简单的架构和更合理的拥有成本以便应对业务的快速增长，但总结以上主流的方案存在主要问题包括：',
    challenges: [
      { title: '数据库一体机成本高，运维复杂，弹性差，无法支持多数据库。', desc: '' },
      { title: '物理服务器加集中式存储的方案，在成本、易维护性和敏捷性方面比较差。', desc: '' },
      { title: 'VMware 加集中存储的方案，在性能与延迟方面与裸金属以及数据库一体机方案仍有差距，同时存储扩展性和整体成本方面仍然不能更好满足用户需求。', desc: '' },
    ],
    scenarios: [
      { title: '一、多类型数据库统一管控场景', desc: '适用于企业多类型、多版本、多规格数据库并存的场景，实现各类数据库的云端统一部署、监控与管理，简化运维流程，降低管理成本。' },
      { title: '二、AI及高并发业务数据库支撑场景', desc: '适配AI应用、高频交易等高并发场景，通过云端弹性资源调度，满足数据库高算力、高IO需求，提升业务响应速度。' },
      { title: '三、混合数据库架构协同场景', desc: '适用于“云端+本地”混合数据库部署模式，实现云端与本地数据实时同步、互联互通，支撑跨系统业务数据顺畅流转。' },
      { title: '四、轻量化数据库快速部署场景', desc: '针对企业一般应用数据库，提供云端快速部署、弹性扩缩容能力，适配轻量化业务需求，减少资源浪费。' },
    ],
    architectureAsset: 'database-cloud-architecture.png',
    advantages: [
      { title: '1、极致性能', desc: '从虚拟化、接入层、缓存层到存储网络端到端性能优化，并针对不同数据库类型实现性能优化与部署的最佳实践。' },
      { title: '2、可靠可控', desc: '核心基于自主研发的分布式块存储，可靠性与企业级特性经过金融生产环境长时间检验。' },
      { title: '3、多场景与生态适配', desc: '覆盖核心数据库、主流一般业务数据库（含信创）到云原生数据库多种场景解决方案，提供广泛的产品适配、性能优化，以及数据库云化管理集成。' },
      { title: '4、多场景与生态适配', desc: '覆盖核心数据库、主流一般业务数据库（含信创）到云原生数据库多种场景解决方案，提供广泛的产品适配、性能优化，以及数据库云化管理集成。' },
    ],
    caseBackground:
      '某区域银行面临数据库种类多、规格杂、数据量差异大的现状：银行有6套3TB的Oracle RAC支撑核心账务，100余套100GB-2TB的MySQL支撑一般生产业务；医院核心HIS系统用1.4TB的Oracle RAC，5个应用用80GB-1TB的Oracle，17个系统用80GB-800GB的SQL Server，无纸化病案用400GB的MongoDB，原有本地部署架构运维繁琐、资源浪费，且难以满足合规与高可用需求，亟需通过云化基础设施实现优化。',
    caseDifficulties: [
      '一是多类型、多规格数据库兼容适配难，Oracle、MySQL、SQL Server、MongoDB差异大，与云平台适配易出现功能异常；',
      '二是核心Oracle RAC数据量大，迁移风险高，需保障银行账务、医院HIS系统等核心业务不中断；',
      '三是银行有严格合规要求，云端数据安全、备份需符合行业规范；四是数据库资源需求差异大，需平衡核心与一般数据库的资源分配，避免浪费与性能瓶颈。',
    ],
    caseSolutions: [
      '部署数据库云化基础设施，采用统一管控平台兼容各类数据库，实现云端统一部署与运维，降低管理成本；核心Oracle RAC采用分布式存储构建高可用架构，结合全量+增量迁移模式，保障迁移过程中业务连续、数据一致，避免中断与丢失问题；搭建合规型云端存储体系，实现数据加密、定期备份与审计，满足金融行业合规要求；采用弹性资源调度机制，为核心数据库分配高性能资源，为一般数据库按需分配资源，同时实现云端与本地数据实时同步，支撑跨系统业务流转，提升资源利用率。',
    ],
  },
  'compute-network-infrastructure': {
    title: '算力网络解决方案',
    background:
      '随着人工智能、大数据及各类新型数字化业务快速发展，全社会算力需求持续激增，异构算力资源分散孤立、跨域调度困难、资源利用率偏低等问题日益突出。结合东数西算战略落地与算网融合发展趋势，传统网络架构已无法满足算力泛在接入、按需调度、协同共享的诉求，亟需建设一体化算力网络解决方案，打通全域算力与网络资源，实现算力统一编排、灵活调度与高效协同，全面支撑企业数字化转型与 AI 业务规模化落地。',
    challenges: [
      { title: '算力资源碎片化', desc: '各类通用算力、AI 算力、边缘算力分散部署，异构资源无法互通共享，算力孤岛现象严重，整体资源利用率低下。' },
      { title: '跨域调度能力不足', desc: '跨区域、跨机房、跨节点算力调度链路不通，网络时延高、传输带宽受限，难以支撑大模型、海量数据等高性能业务。' },
      { title: '业务承载压力攀升', desc: 'AI 推理、大数据分析、高清算力业务规模化落地，对基础设施弹性扩展、超低时延、高可靠传输提出更高要求。' },
      { title: '运维管理复杂', desc: '分散算力、网络、存储多套体系独立管理，缺乏统一管控平台，运维成本高，故障排查与资源调度效率低下。' },
    ],
    scenarios: [
      { title: 'AI 算力协同调度场景', desc: '面向大模型训练、智能推理等业务，实现中心智算、边缘算力统一调度，按需分配异构算力，降低算力建设成本。' },
      { title: '算力跨域协同场景', desc: '支撑算力资源协同联动，实现冷数据后台处理、热数据本地计算，优化全域算力资源合理布局。' },
      { title: '行业数字化算力承载场景', desc: '适配政务、医疗、工业制造等行业，为产业数字化、智能制造、远程算力赋能提供稳定泛在的算网支撑。' },
      { title: '边缘分布式算力场景', desc: '满足园区、工厂、分支机构边缘业务低时延需求，实现边缘算力与中心算力双向协同，保障实时业务运行。' },
    ],
    architectureAsset: 'compute-network-architecture.png',
    advantages: [
      { title: '1、算网深度融合，架构一体化', desc: '以算力为核心、网络为根基，融合网云数智安边端能力，打造一体化新型信息基础设施。' },
      { title: '2、算力全域泛在，资源高协同', desc: '构建算网共生体系，破除资源孤岛，实现全域算力随时随地灵活覆盖与互通共享。' },
      { title: '3、全域智能编排，调度更高效', desc: '依托智能调度编排能力，动态适配业务负载，全面提升基础设施整体运行效能。' },
      { title: '4、算力服务化，按需即取即用', desc: '将算力转化为公共基础服务，实现按需供给、弹性复用，降低企业数字化建设成本。' },
    ],
    caseBackground:
      '某大型企业业务覆盖政务服务、产业园区、智慧医疗、AI 创新研发等板块，内部同时部署中心算力、边缘算力、终端算力等异构资源。原有计算、网络、存储、安全系统独立建设，网、云、数、边、端资源相互割裂，形成大量算力孤岛。随着大模型推理、大数据分析、园区边缘实时业务快速落地，跨区域算力调度困难、资源闲置与算力短缺并存，亟需搭建以算为中心、网为根基的一体化算力网络，实现算力泛在供给与统一服务。',
    caseDifficulties: [
      '各类异构算力、多厂商网络设备体系不互通，算网无法联动编排，跨节点资源协同难度大。',
      '东部业务算力需求集中、负载饱和，西部及边缘算力资源闲置，算力供需错配严重。',
      '多场景业务并行运行，对网络时延、传输带宽、数据安全、资源隔离提出差异化要求。',
      '基础设施分散管控，缺少智能调度能力，算力无法实现按需调用、弹性伸缩，运维效率偏低。',
    ],
    caseSolutions: [
      '部署一体化算力网络基础设施，以网络为根基，深度融合网、云、数、智、安、边、端全要素能力，构建统一新型信息底座。',
      '搭建算网智能编排平台，打通跨域、跨节点算力通道，实现全域算力统一感知、动态调度，平衡东西部算力资源。',
      '优化骨干与边缘网络架构，降低跨网传输时延，结合全域安全能力，保障多业务隔离运行与数据安全合规。',
      '落地算力服务化能力，实现算力泛在、算网共生，将算力转为可随时调用的公共服务，支持资源即取即用、弹性扩容，整体提升资源利用率与业务承载能力。',
    ],
  },
}
